import React from 'react';
import { DataService } from 'src/services/data-service';

export interface LovItem {
  id: string;
  name: string;
}

export interface LovDictionary {
  [id: string]: string;
}

export class LovService {
  private static cache: Map<string, LovDictionary> = new Map();

  static async getLovDictionary(
    endpoint: string, 
    useCache: boolean = true
  ): Promise<LovDictionary> {
    if (useCache && this.cache.has(endpoint)) {
      return this.cache.get(endpoint)!;
    }

    try {
      const response = await DataService.get(`api/Lov/${endpoint}`);
      
      if (!response.ok) {
        console.error(`Failed to fetch LOV data for ${endpoint}:`, response.statusText);
        return {};
      }

      const data = await response.json();
      
      let dictionary: LovDictionary = {};
      
      if (Array.isArray(data)) {
        dictionary = this.parseArrayToDictionary(data);
      } else if (typeof data === 'object' && data !== null) {
        dictionary = data;
      } else {
        console.warn(`Unexpected LOV data format for ${endpoint}:`, data);
        return {};
      }

      if (useCache) {
        this.cache.set(endpoint, dictionary);
      }

      return dictionary;
    } catch (error) {
      console.error(`Error fetching LOV data for ${endpoint}:`, error);
      return {};
    }
  }

  private static parseArrayToDictionary(data: any[]): LovDictionary {
    const dictionary: LovDictionary = {};
    
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        const id = item.id || item.Id || item.ID || item.value || item.key;
        const name = item.name || item.Name || item.title || item.label || item.text || item.description || item.code;
        
        if (id && name) {
          dictionary[id.toString()] = name.toString();
        } else {
          console.warn('LOV item missing id or name properties:', item);
        }
      }
    });
    
    return dictionary;
  }

  static async getLovArray(endpoint: string, useCache: boolean = true): Promise<LovItem[]> {
    const dictionary = await this.getLovDictionary(endpoint, useCache);
    
    return Object.entries(dictionary).map(([id, name]) => ({
      id,
      name
    }));
  }

  static clearCache(endpoint?: string): void {
    if (endpoint) {
      this.cache.delete(endpoint);
    } else {
      this.cache.clear();
    }
  }

  static async preloadLovs(endpoints: string[]): Promise<void> {
    const promises = endpoints.map(endpoint => 
      this.getLovDictionary(endpoint).catch(error => 
        console.error(`Failed to preload LOV ${endpoint}:`, error)
      )
    );
    
    await Promise.allSettled(promises);
  }
}

export const useLov = (endpoint: string, autoLoad: boolean = true) => {
  const [data, setData] = React.useState<LovDictionary>({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loadLov = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await LovService.getLovDictionary(endpoint);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load LOV data');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  React.useEffect(() => {
    if (autoLoad && endpoint) {
      loadLov();
    }
  }, [autoLoad, endpoint, loadLov]);

  return {
    data,
    loading,
    error,
    reload: loadLov,
    clearCache: () => LovService.clearCache(endpoint)
  };
};